import type { MouseEventHandler, ReactElement } from "react";
import { useMemo, useState } from "react";
import { IconAlignment, Level, Size } from "../UiEnums";
import classNames from "classnames";
import "./Buttons.scss";
import "@/shared/style/design_system/Fonts.scss";
import type { IconComponent } from "@/shared/components/generic/IconComponentType.ts";
import { debounce } from "@mui/material";

interface Properties {
  icon?: IconComponent;
  alignIcon?: IconAlignment;
  onClick: MouseEventHandler<HTMLElement>;
  level?: Level;
  size?: Size;
  disabled?: boolean;
  label: string;
  debounceDuration?: number;
}

export default function Button({
  icon,
  alignIcon = IconAlignment.LEFT,
  onClick,
  level = Level.PRIMARY,
  size = Size.MEDIUM,
  disabled = false,
  label = "",
  debounceDuration = 500,
}: Properties): ReactElement {
  function limitSize(): Size {
    // Cap generic button size to what's defined in design system:
    const capSize = (): Size => {
      switch (size) {
        case Size.XXLARGE:
        case Size.XXXLARGE:
          return Size.XLARGE;
        case Size.XSMALL:
          return Size.SMALL;
        default:
          return size;
      }
    };
    const cappedSize = capSize();
    if (cappedSize !== size) {
      console.warn(
        `Button isn't available for size ${size}, using ${cappedSize} instead`,
      );
    }
    return cappedSize;
  }

  function sizeClass(): string {
    return Size[limitSize()].toLocaleLowerCase();
  }

  function iconClasses(): string | undefined {
    if (icon !== undefined) {
      return classNames("icon", sizeClass());
    }
  }

  function buttonClasses(): string {
    return classNames(
      "button",
      "body",
      Level[level].toLocaleLowerCase(),
      sizeClass(),
      disabled && "disabled",
    );
  }

  const iconComponent = icon?.({ className: iconClasses() });

  const [isClickable, setIsClickable] = useState(true);

  const debouncedClickHandler = useMemo(
    () =>
      debounce((): void => {
        setIsClickable(true);
      }, debounceDuration),
    [debounceDuration],
  );

  return (
    <div
      className={classNames(buttonClasses())}
      onClick={(event: React.MouseEvent<HTMLElement>): void => {
        if (isClickable) {
          setIsClickable(false);
          onClick(event);
          debouncedClickHandler();
        }
      }}
    >
      {icon !== undefined && alignIcon === IconAlignment.LEFT && iconComponent}
      <div>{label}</div>
      {icon !== undefined && alignIcon === IconAlignment.RIGHT && iconComponent}
    </div>
  );
}
